import axios from 'axios';
import { acquireToken } from '../../../../../auth/msal';
import { protectedResourcesScopes } from '../../../../../auth/config';


const scope = protectedResourcesScopes.core;
const apiUrl = `https://meris-backend.ashyflower-93837a9b.brazilsouth.azurecontainerapps.io`;
// const apiUrl = `http://localhost:3333`;

export interface Manufacturer {
  id: string;
  name: string;
}

export interface InstrumentCategory {
  id: string;
  label: string;
  value: string;
  properties?: CategoryProperty[];
}

export interface CategoryProperty {
  id: string;
  name: string;
  type: 'string' | 'number' | 'enum';
  required: boolean;
  enumValues?: string[];
}

export interface InstrumentProperty {
  id: string;
  name: string;
  type: string;
  value: any;
  required?: boolean;
}

export interface CreateInstrumentRequest {
  name: string;
  manufacturer: string;
  category: string;
  certificate_expiration_date: string;
  serialNumber: string;
  modelNumber: string;
  certificateNumber: string;
  properties: InstrumentProperty[];
  plantId: string;
}

export interface Instrument {
  _id: string;
  name: string;
  manufacture: {
    _id: string;
    name: string;
  };
  category: {
    _id: string;
    name: string;
  };
  certificate_expiration_date?: string;
  serialNumber?: string;
  modelNumber?: string;
  certificateNumber?: string;
  properties?: InstrumentProperty[];
  plantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetInstrumentsParams {
  limit?: number;
  offset?: number;
  _id?: string;
  search?: string;
  plantId?: string;
}

export interface InstrumentsResponse {
  items: Instrument[];
  total: number;
  page: number;
  limit: number;
  uniqueValues?: Record<string, any[]>;
}

export async function getManufacturers(instance: any): Promise<Manufacturer[]> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { data } = await axios.get(`${apiUrl}/instruments/manufacturers`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function getInstruments(
  instance: any,
  params?: GetInstrumentsParams
): Promise<InstrumentsResponse> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { plantId, ...queryParams } = params || {};

    if (!plantId) {
      throw new Error('Plant ID is required');
    }

    const { data } = await axios.get(`${apiUrl}/instruments/${plantId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: queryParams,
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function getInstrumentById(instance: any, plantId: string, instrumentId: string): Promise<Instrument> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { data } = await axios.get(`${apiUrl}/instruments/${plantId}/${instrumentId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function createInstrument(
  instance: any,
  instrument: CreateInstrumentRequest
): Promise<Instrument> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { plantId, ...instrumentData } = instrument;

    const { data } = await axios.post(`${apiUrl}/instruments/${plantId}`, instrumentData, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function updateInstrument(
  instance: any,
  plantId: string,
  instrumentId: string,
  instrument: Partial<CreateInstrumentRequest>
): Promise<Instrument> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { plantId: _, ...instrumentData } = instrument;

    const { data } = await axios.put(`${apiUrl}/instruments/${plantId}/${instrumentId}`, instrumentData, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function deleteInstrument(instance: any, plantId: string, instrumentId: string): Promise<void> {
  try {
    const accessToken = await acquireToken(instance, scope);
    await axios.delete(`${apiUrl}/instruments/${plantId}/${instrumentId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    throw error;
  }
}

export async function getInstrumentCategories(instance: any): Promise<InstrumentCategory[]> {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { data } = await axios.get(`${apiUrl}/instruments/categories`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

export async function getInstrument(instance: any, plantId: string, instrumentId: string): Promise<Instrument> {
  return getInstrumentById(instance, plantId, instrumentId);
}

// Função para buscar documentos de um instrumento
export async function getInstrumentFiles(instance: any, plantId: string, instrumentId: string) {
  try {
    const accessToken = await acquireToken(instance, scope);
    const { data } = await axios.get(`${apiUrl}/instruments/${plantId}/${instrumentId}/files`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

// Função para fazer upload de documento para um instrumento
export async function uploadInstrumentFile(instance: any, plantId: string, instrumentId: string, file: File) {
  try {
    const accessToken = await acquireToken(instance, scope);
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await axios.post(`${apiUrl}/instruments/${plantId}/${instrumentId}/upload`, formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return data;
  } catch (error) {
    throw error;
  }
}

